/**
 * LocationSwitcher — sidebar dropdown for multi-location operators.
 *
 * Lists every business under this owner's email + lets you switch between
 * them. "+ Add new location" opens a modal with two paths:
 *
 *   • Start clean — blank Mimi brain, forces the operator to fill in
 *     cuisine/dietary/parking/training from scratch. Best when the new
 *     spot is genuinely a different concept.
 *
 *   • Copy from an existing location — duplicates the operator's already-
 *     filled Guest FAQ, cuisine, dietary, dress code, parking, and
 *     optionally the trained Q&A pairs. NEVER copies address, hours,
 *     Twilio number, table inventory, or manager phone — those MUST be
 *     unique per location and are enforced blank so the operator has to
 *     enter real values.
 *
 * Soft cap of 4 locations per account. Above that, the dropdown swaps the
 * "Add new location" button for a mailto link to the founder for
 * Enterprise pricing.
 */
import { useEffect, useState } from "react";
import { api, setToken } from "../lib/api";
import { CaretDown, Plus, Buildings, X, Copy, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LocationSwitcher({ business }) {
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [limits, setLimits] = useState({ count: 1, cap: 4, remaining: 3, at_cap: false });
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    business_name: "",
    industry: business?.industry || "restaurant",
    mode: "clean",              // "clean" | "copy"
    copy_from_location_id: "",
    copy_training: true,
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [rL, rLim] = await Promise.all([
        api.get("/locations"),
        api.get("/locations/limits"),
      ]);
      setLocations(rL.data || []);
      setLimits(rLim.data);
    } catch (err) {
      console.error("Load locations failed:", err);
    }
  };

  useEffect(() => { load(); }, []);

  const switchTo = async (id) => {
    if (id === business.id) { setOpen(false); return; }
    try {
      const r = await api.post(`/locations/switch/${id}`, {});
      setToken(r.data.token);
      toast.success(`Switched to ${r.data.business.business_name}`);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Switch failed");
    }
  };

  const createLocation = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim()) return toast.error("Enter a name for the new location");
    if (form.mode === "copy" && !form.copy_from_location_id) {
      return toast.error("Pick which location to copy from");
    }
    setBusy(true);
    try {
      const payload = {
        business_name: form.business_name,
        industry: form.industry,
        copy_from_location_id: form.mode === "copy" ? form.copy_from_location_id : null,
        copy_training: form.mode === "copy" ? form.copy_training : false,
      };
      const r = await api.post("/locations", payload);
      setToken(r.data.token);
      const copied = r.data.training_copied || 0;
      const trainingNote = copied
        ? ` · copied ${copied} training pair${copied === 1 ? "" : "s"}`
        : "";
      toast.success(`Created ${r.data.business.business_name} — switched.${trainingNote}`);
      // Fresh location = fresh onboarding checklist. DashboardLayout auto-
      // opens the copilot when the onboarding progress is low, which it
      // will be for this brand-new business. Reload to reboot state.
      window.location.href = "/dashboard?new_location=1";
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Create failed";
      if (status === 402) {
        toast.error(detail, {
          action: {
            label: "Contact founder",
            onClick: () => {
              window.location.href = "mailto:broadheadmiles@gmail.com?subject=Enterprise%20pricing%20for%20multi-location%20BuzzerAI";
            },
          },
          duration: 10000,
        });
      } else {
        toast.error(detail);
      }
    }
    setBusy(false);
  };

  const hasMultiple = locations.length > 1;
  const siblings = locations.filter((l) => l.id !== business.id);

  return (
    <div className="relative mt-1" data-testid="location-switcher">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 text-left rounded-lg hover:bg-[#E6E2D6] -mx-2 px-2 py-1 transition-colors"
        data-testid="location-switcher-toggle"
      >
        <span className="font-heading font-extrabold text-lg flex-1 truncate">{business.business_name}</span>
        {hasMultiple && <CaretDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#E1DFD5] rounded-lg shadow-lg overflow-hidden">
          {locations.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => switchTo(l.id)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[#E6E2D6] flex items-center gap-2 ${l.id === business.id ? "bg-[#C2D1C1]/40" : ""}`}
              data-testid={`location-item-${l.slug}`}
            >
              <Buildings size={14} color="#1E3A2F" />
              <span className="flex-1 truncate">{l.business_name}</span>
              {l.id === business.id && <span className="text-[10px] text-[#5C6B61]">current</span>}
            </button>
          ))}
          <div
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#5C6B61] border-t border-[#E1DFD5] bg-[#F7F6F2]"
            data-testid="location-count-label"
          >
            {limits.count} of {limits.cap} locations used
          </div>
          {limits.at_cap ? (
            <a
              href="mailto:broadheadmiles@gmail.com?subject=Enterprise%20pricing%20for%20multi-location%20BuzzerAI"
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#E6E2D6] flex items-center gap-2 text-[#5C6B61]"
              data-testid="location-cap-reached"
            >
              <Sparkle size={14} weight="fill" /> At cap · email founder for Enterprise
            </a>
          ) : (
            <button
              type="button"
              onClick={() => { setAddOpen(true); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm border-t border-[#E1DFD5] hover:bg-[#E6E2D6] flex items-center gap-2 text-[#1E3A2F]"
              data-testid="location-add-btn"
            >
              <Plus size={14} /> Add new location
            </button>
          )}
        </div>
      )}

      {addOpen && (
        <div
          className="fixed inset-0 z-50 bg-[#121914]/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setAddOpen(false)}
        >
          <div
            className="bg-white rounded-2xl border border-[#E1DFD5] shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="location-add-modal"
          >
            <div className="p-5 border-b border-[#E1DFD5] flex items-center gap-2 sticky top-0 bg-white z-10">
              <Buildings size={20} color="#1E3A2F" weight="fill" />
              <div className="flex-1 font-heading font-extrabold text-lg">Add a new location</div>
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                className="w-9 h-9 rounded-full hover:bg-[#F1D7D2] flex items-center justify-center"
                data-testid="location-add-close"
              >
                <X size={16} />
              </button>
            </div>
            <form onSubmit={createLocation} className="p-5 space-y-4">
              <p className="text-xs text-[#5C6B61] leading-snug">
                Each location gets its <b className="text-[#121914]">own Mimi brain</b> — its own trained Q&amp;A, Guest FAQ, hours, address, Twilio number, table plan, and public /join page. Everything is siloed automatically.
              </p>

              <div>
                <label className="overline block mb-1">Location name</label>
                <input
                  required
                  className="input-field"
                  placeholder="Your new spot's name"
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  data-testid="location-add-name"
                />
              </div>

              <div>
                <label className="overline block mb-1">Industry</label>
                <select
                  className="input-field"
                  value={form.industry}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  data-testid="location-add-industry"
                >
                  <option value="restaurant">Restaurant</option>
                  <option value="salon">Salon / Barber</option>
                  <option value="clinic">Clinic</option>
                  <option value="retail">Retail</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="border-t border-[#E1DFD5] pt-4">
                <div className="overline mb-2">Starting point</div>

                <label
                  className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer mb-2 ${form.mode === "clean" ? "border-[#1E3A2F] bg-[#C2D1C1]/30" : "border-[#E1DFD5]"}`}
                  data-testid="location-add-mode-clean"
                >
                  <input
                    type="radio"
                    name="mode"
                    value="clean"
                    checked={form.mode === "clean"}
                    onChange={() => setForm({ ...form, mode: "clean" })}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-semibold">Start clean</div>
                    <div className="text-xs text-[#5C6B61] mt-0.5">
                      Fresh Mimi brain — you'll fill in cuisine, dietary, parking, training, etc. from scratch. Best for a genuinely different concept.
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${form.mode === "copy" ? "border-[#1E3A2F] bg-[#C2D1C1]/30" : "border-[#E1DFD5]"} ${siblings.length === 0 ? "opacity-50" : ""}`}
                  data-testid="location-add-mode-copy"
                >
                  <input
                    type="radio"
                    name="mode"
                    value="copy"
                    checked={form.mode === "copy"}
                    onChange={() => setForm({ ...form, mode: "copy" })}
                    disabled={siblings.length === 0}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold flex items-center gap-1.5">
                      <Copy size={12} /> Copy from an existing location
                    </div>
                    <div className="text-xs text-[#5C6B61] mt-0.5">
                      Copies cuisine, menu, dietary, dress code, parking, and other shared facts. <b>Never</b> copies address, hours, Twilio number, table inventory, or manager phone — those must be unique.
                    </div>
                    {form.mode === "copy" && siblings.length > 0 && (
                      <div className="mt-2 space-y-2">
                        <select
                          className="input-field !py-1.5 text-sm"
                          value={form.copy_from_location_id}
                          onChange={(e) => setForm({ ...form, copy_from_location_id: e.target.value })}
                          data-testid="location-add-copy-source"
                        >
                          <option value="">— Pick a source location —</option>
                          {siblings.map((l) => (
                            <option key={l.id} value={l.id}>{l.business_name}</option>
                          ))}
                        </select>
                        <label className="flex items-center gap-2 text-xs text-[#121914] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.copy_training}
                            onChange={(e) => setForm({ ...form, copy_training: e.target.checked })}
                            data-testid="location-add-copy-training"
                          />
                          Also copy Mimi's trained Q&amp;A pairs (recommended — you can delete individual pairs later)
                        </label>
                      </div>
                    )}
                    {siblings.length === 0 && (
                      <div className="text-[10px] text-[#5C6B61] mt-1 italic">
                        No other locations yet to copy from.
                      </div>
                    )}
                  </div>
                </label>
              </div>

              <button
                type="submit"
                disabled={busy}
                className="btn-primary w-full justify-center"
                data-testid="location-add-submit"
              >
                {busy ? "Creating…" : "Create & switch"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
